<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import MessagePreview from './MessagePreview.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import UnreadBadge from 'dashboard/components-next/Conversation/ConversationCard/UnreadBadge.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  isActiveChat: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
  showInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});

const emit = defineEmits([
  'click',
  'contextmenu',
  'selectConversation',
  'deSelectConversation',
]);

const { t } = useI18n();

const hovered = ref(false);

const unreadCount = computed(() => props.chat.unread_count);
const hasUnread = computed(() => unreadCount.value > 0);
const lastMessageInChat = computed(() => getLastMessage(props.chat));

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

const isAIAssignee = computed(() =>
  ['AgentBot', 'Captain::Assistant'].includes(props.chat?.meta?.assignee_type)
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !props.currentContact?.blocked
);

const showLabelsSection = computed(() => {
  return props.chat.labels?.length > 0 || hasSlaPolicyId.value;
});

const showAssigneeName = computed(
  () => props.showAssignee && !!props.assignee.name
);

// Conversas que vieram de um ticket do WHMCS.
const whmcsTicket = computed(() => props.chat.additional_attributes?.whmcs);

// Reembolso em andamento (Refunds::FlowService); concluido nao mostra selo.
const refund = computed(() => {
  const data = props.chat.additional_attributes?.refund;
  if (!data || data.status === 'completed') return null;
  if (data.status === 'approved' && data.due_on) {
    const [, month, day] = data.due_on.split('-');
    return t('CHAT_LIST.REFUND.APPROVED', { date: `${day}/${month}` });
  }
  return t('CHAT_LIST.REFUND.ANALYSIS');
});

const showFooter = computed(
  () =>
    showLabelsSection.value ||
    showAssigneeName.value ||
    !!whmcsTicket.value ||
    !!refund.value
);

const messagePreviewClass = computed(() =>
  hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11'
);

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, props.inbox.id);
  } else {
    emit('deSelectConversation', props.chat.id, props.inbox.id);
  }
};

const selectedModel = computed({
  get: () => props.selected,
  set: value => onSelectConversation(value),
});

watch(
  () => props.chat.id,
  () => {
    hovered.value = false;
  }
);
</script>

<template>
  <div
    class="relative flex items-start gap-3 py-3 mx-2 my-0.5 transition-colors cursor-pointer conversation rounded-xl group"
    :class="[
      compact ? 'px-1' : 'px-3',
      {
        'active bg-n-blue-2 dark:bg-n-blue-3': isActiveChat,
        'selected bg-n-alpha-2': selected && !isActiveChat,
        'hover:bg-n-alpha-1': !isActiveChat && !selected,
      },
    ]"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <div
      v-if="!hideThumbnail"
      class="relative flex-shrink-0"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="40"
        :status="currentContact.availability_status"
        hide-offline-status
      >
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <Checkbox v-model="selectedModel" />
          </label>
        </template>
      </Avatar>
      <span
        v-if="showInboxName && inbox.id"
        v-tooltip.top="inbox.name"
        class="absolute grid rounded-full -bottom-0.5 -end-0.5 size-5 place-content-center bg-n-background ring-2 ring-n-surface-1 text-n-slate-11"
      >
        <ChannelIcon :inbox="inbox" use-brand-icon class="size-3" />
      </span>
    </div>
    <div class="flex-1 min-w-0">
      <div class="flex items-center min-w-0 gap-2">
        <h4
          class="flex-1 min-w-0 my-0 text-sm capitalize truncate conversation--user text-n-slate-12"
          :class="hasUnread ? 'font-semibold' : 'font-medium'"
        >
          {{ currentContact.name }}
        </h4>
        <CardPriorityIcon
          :priority="chat.priority"
          class="flex-shrink-0 !size-3.5"
        />
        <span class="flex-shrink-0 leading-4 text-xxs text-n-slate-10">
          <TimeAgo
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
            :conversation-id="chat.id"
          />
        </span>
      </div>
      <div class="flex items-center min-w-0 gap-2 mt-0.5">
        <VoiceCallStatus
          v-if="voiceCallData.status"
          key="voice-status-row"
          class="flex-1 min-w-0"
          :status="voiceCallData.status"
          :direction="voiceCallData.direction"
          :message-preview-class="messagePreviewClass"
        />
        <MessagePreview
          v-else-if="lastMessageInChat"
          key="message-preview"
          :message="lastMessageInChat"
          class="flex-1 h-5 min-w-0 my-0 text-sm leading-5"
          :class="messagePreviewClass"
        />
        <p
          v-else
          key="no-messages"
          class="flex-1 h-5 min-w-0 my-0 overflow-hidden text-sm leading-5 text-n-slate-11 text-ellipsis whitespace-nowrap"
        >
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </p>
        <UnreadBadge
          v-if="hasUnread"
          :count="unreadCount"
          class="flex-shrink-0"
        />
      </div>
      <div v-if="showFooter" class="flex items-center min-w-0 gap-2 mt-1.5">
        <a
          v-if="whmcsTicket"
          v-tooltip.top="whmcsTicket.subject"
          :href="whmcsTicket.url"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center flex-shrink-0 h-5 gap-1 px-1.5 text-xs font-medium rounded-md bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12"
          @click.stop
        >
          <Icon icon="i-lucide-ticket" class="flex-shrink-0 size-3" />
          {{ `${whmcsTicket.department} · ${whmcsTicket.tid}` }}
        </a>
        <span
          v-if="refund"
          class="inline-flex items-center flex-shrink-0 h-5 gap-1 px-1.5 text-xs font-medium rounded-md bg-n-amber-3 text-n-amber-11"
        >
          <Icon icon="i-lucide-hand-coins" class="flex-shrink-0 size-3" />
          {{ refund }}
        </span>
        <CardLabels
          v-if="showLabelsSection"
          :conversation-labels="chat.labels"
          class="flex-1 min-w-0 m-0"
        >
          <template v-if="hasSlaPolicyId" #before>
            <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
          </template>
        </CardLabels>
        <span
          v-if="showAssigneeName"
          class="inline-flex items-center gap-1 text-xs truncate ms-auto max-w-[45%] text-n-slate-10"
        >
          <Icon
            :icon="isAIAssignee ? 'i-lucide-bot' : 'i-lucide-user-round'"
            class="flex-shrink-0 size-3"
          />
          <span class="truncate">{{ assignee.name }}</span>
        </span>
      </div>
    </div>
  </div>
</template>
